import { Alert, Button, EmptyState, Module, ModuleBody, Spinner } from "@vjal/ui";
import { useId } from "react";
import type { ActionCapabilityDecision, RouteAccessDecision } from "../domain/capabilities";

export function RouteAccessState({
  decision,
  layout = "page",
  showHeading = true
}: {
  decision: RouteAccessDecision;
  layout?: "page" | "module";
  showHeading?: boolean;
}) {
  if (decision.allowed) return null;

  const body = (
    <Module className="w-full" span={layout === "module" ? 12 : undefined}>
      <ModuleBody>
        {showHeading ? <h1 className="sr-only">This page is not available.</h1> : null}
        <EmptyState title="This page is not available.">{decision.reason}</EmptyState>
      </ModuleBody>
    </Module>
  );

  if (layout === "module") return body;

  return <section className="mx-auto grid min-h-dvh max-w-2xl place-items-center px-6">{body}</section>;
}

// Branded interstitial for the live record-scope resolver. While scope is still
// RESOLVING we must not render record data, but we must not show denial copy
// either: "This page is not available." only renders after resolution actually
// denies. This keeps the 2-4s cold-load window honest for in-scope users.
// The same interstitial covers the live auth-hydration window with its own
// message, so cold deep-route loads never flash the login shell.
export function RouteScopeResolvingState({ message = "Checking access to this record…" }: { message?: string }) {
  return (
    <section className="mx-auto grid min-h-dvh max-w-2xl place-items-center px-6">
      <Module className="w-full">
        <ModuleBody>
          <div
            className="grid place-items-center gap-3 py-10 text-center"
            role="status"
            aria-live="polite"
            aria-label={message}
          >
            <Spinner size="lg" label={null} />
            <p className="m-0 text-sm leading-6 text-ink-muted">{message}</p>
          </div>
        </ModuleBody>
      </Module>
    </section>
  );
}

export function ActionAccessState({ decision }: { decision: ActionCapabilityDecision }) {
  if (decision.allowed || decision.presentation === "hidden") return null;

  return (
    <Alert variant={decision.presentation === "disabled-unavailable" ? "warning" : "info"}>
      {decision.reason}
    </Alert>
  );
}

export function CapabilityButton({
  decision,
  children,
  onClick,
  loading = false,
  "data-testid": testId
}: {
  decision: ActionCapabilityDecision;
  children: string;
  onClick?: () => void;
  loading?: boolean;
  "data-testid"?: string;
}) {
  const reasonId = useId();
  if (decision.presentation === "hidden") return null;

  // The capability decision stays authoritative: a denied decision renders a
  // disabled control with its accessible reason and never receives the click
  // handler, so wiring a mutation can never widen role access.
  return (
    <div className="grid gap-2">
      <Button
        disabled={!decision.allowed}
        aria-describedby={!decision.allowed ? reasonId : undefined}
        type="button"
        loading={decision.allowed ? loading : false}
        onClick={decision.allowed ? onClick : undefined}
        data-testid={testId}
      >
        {children}
      </Button>
      {!decision.allowed ? <DisabledControlReason id={reasonId}>{decision.reason}</DisabledControlReason> : null}
    </div>
  );
}

export function DisabledControlReason({ children, id }: { children: string; id?: string }) {
  return (
    <p id={id} className="m-0 text-xs leading-5 text-ink-muted">
      {children}
    </p>
  );
}
